//SiteHealthScore.js
import React, { useState, useEffect } from 'react';

// Function to calculate site health score from raw feedback data with fallback logic
export const calculateSiteHealthScore = (feedbackData) => {
  if (!feedbackData || feedbackData.length === 0) {
    console.log("ending here")
    return { score: 0, trends: { lastWeek: 0, lastMonth: 0 } };
  }

  // Get current date and calculate dates for comparison periods
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);
  
  const twoWeeksAgo = new Date(now);
  twoWeeksAgo.setDate(now.getDate() - 14);
  
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);
  
  const twoMonthsAgo = new Date(now);
  twoMonthsAgo.setMonth(now.getMonth() - 2);

  // Filter data for different time periods
  const currentWeekData = feedbackData.filter(item => 
    new Date(item.feedback_time) >= oneWeekAgo);
  console.log('currentWeekData', currentWeekData)
  
  const lastWeekData = feedbackData.filter(item => 
    new Date(item.feedback_time) >= twoWeeksAgo && 
    new Date(item.feedback_time) < oneWeekAgo);
  
  const lastMonthData = feedbackData.filter(item => 
    new Date(item.feedback_time) >= oneMonthAgo && 
    new Date(item.feedback_time) < now);
    
  const twoMonthsAgoData = feedbackData.filter(item => 
    new Date(item.feedback_time) >= twoMonthsAgo && 
    new Date(item.feedback_time) < oneMonthAgo);

  // Calculate current score with fallback logic
  let currentScore = calculatePeriodScore(currentWeekData);
  
  // If no current week data, fall back to last week, then last month, then all available data
  if (currentScore === 0) {
    currentScore = calculatePeriodScore(lastWeekData);
    if (currentScore === 0) {
      currentScore = calculatePeriodScore(lastMonthData);
      if (currentScore === 0) {
        currentScore = calculatePeriodScore(feedbackData); // Use all available data
      }
    }
  }
  
  console.log('currentScore', currentScore)

  // Calculate comparison scores with fallback logic
  let weekComparisonScore = calculatePeriodScore(lastWeekData);
  if (weekComparisonScore === 0) {
    // If no last week data, use data from 2 weeks ago, then last month, then oldest available
    weekComparisonScore = calculatePeriodScore(feedbackData.filter(item => 
      new Date(item.feedback_time) >= twoWeeksAgo && 
      new Date(item.feedback_time) < oneWeekAgo));
    
    if (weekComparisonScore === 0) {
      weekComparisonScore = calculatePeriodScore(twoMonthsAgoData);
      if (weekComparisonScore === 0) {
        // Use the oldest half of available data as comparison
        const sortedData = [...feedbackData].sort((a, b) => 
          new Date(a.feedback_time) - new Date(b.feedback_time));
        const halfPoint = Math.floor(sortedData.length / 2);
        weekComparisonScore = calculatePeriodScore(sortedData.slice(0, halfPoint));
      }
    }
  }
  
  console.log('weekComparisonScore', weekComparisonScore)

  let monthComparisonScore = calculatePeriodScore(twoMonthsAgoData);
  if (monthComparisonScore === 0) {
    // If no data from 2 months ago, use older data or split available data
    const sortedData = [...feedbackData].sort((a, b) => 
      new Date(a.feedback_time) - new Date(b.feedback_time));
    const halfPoint = Math.floor(sortedData.length / 2);
    monthComparisonScore = calculatePeriodScore(sortedData.slice(0, halfPoint));
  }

  // Calculate trend percentages
  const weekTrend = weekComparisonScore > 0 
    ? ((currentScore - weekComparisonScore) / weekComparisonScore) * 100 
    : 0;
  console.log('weekTrend', weekTrend)
  
  const monthTrend = monthComparisonScore > 0 
    ? ((currentScore - monthComparisonScore) / monthComparisonScore) * 100 
    : 0;

  return {
    score: currentScore,
    trends: {
      lastWeek: weekTrend,
      lastMonth: monthTrend
    }
  };
};

// Calculate score for a specific period
const calculatePeriodScore = (periodData) => {
  if (!periodData || periodData.length === 0) return 0;
  
  let ratingSum = 0;
  let ratingCount = 0;
  let yesCount = 0;
  let noCount = 0;

  // Process different types of feedback
  periodData.forEach(item => {
    if (item.response_type === 'rating' && item.rating !== null) {
      ratingSum += parseFloat(item.rating);
      ratingCount++;
    } else if (item.response_type === 'yes_no') {
      if (item.yes_no === 'yes') yesCount++;
      else if (item.yes_no === 'no') noCount++;
    }
  });

  // Calculate average rating (scale 1-5)
  const avgRating = ratingCount > 0 ? ratingSum / ratingCount : 0;
  
  // Calculate yes ratio (scale 0-1)
  const yesNoTotal = yesCount + noCount;
  const yesRatio = yesNoTotal > 0 ? yesCount / yesNoTotal : 0;
  
  // Normalize rating to 0-1 scale from 1-5 scale
  const normalizedRating = ratingCount > 0 ? (avgRating - 1) / 4 : 0;
  
  // Weight factors (can be adjusted based on importance)
  const ratingWeight = 0.7;
  const yesNoWeight = 0.3;
  
  // Calculate composite score (0-100 scale)
  let compositeScore = 0;
  
  if (ratingCount > 0 && yesNoTotal > 0) {
    // Both types of feedback exist
    compositeScore = (normalizedRating * ratingWeight + yesRatio * yesNoWeight) * 100;
  } else if (ratingCount > 0) {
    // Only ratings exist
    compositeScore = normalizedRating * 100;
  } else if (yesNoTotal > 0) {
    // Only yes/no exists
    compositeScore = yesRatio * 100;
  }
  
  return Math.round(compositeScore);
};

// Helper function to determine health status text and color
const getHealthStatus = (score) => {
  if (score >= 80) return { text: 'Excellent', color: '#10B981' }; // Green
  if (score >= 70) return { text: 'Good', color: '#6EE7B7' }; // Light Green
  if (score >= 60) return { text: 'Average', color: '#FBBF24' }; // Yellow
  if (score >= 50) return { text: 'Needs Attention', color: '#F59E0B' }; // Orange
  return { text: 'Critical', color: '#EF4444' }; // Red
};

// Helper function to format trend values
const formatTrend = (value) => {
  if (value === 0) return '0%';
  return `${value > 0 ? '+' : ''}${Math.round(value)}%`;
};

const SiteHealthScore = ({siteId}) => {
  const [healthData, setHealthData] = useState({
    score: 0,
    trends: { lastWeek: 0, lastMonth: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch feedback data for all 3 deck positions
        const q1Response = await fetch(`/v2/question_1?site_id=${siteId}`);
        const q2Response = await fetch(`/v2/question_2?site_id=${siteId}`);
        const q3Response = await fetch(`/v2/question_3?site_id=${siteId}`);
        
        const q1Data = await q1Response.json();
        const q2Data = await q2Response.json();
        const q3Data = await q3Response.json();
        
        // Combine all feedback data
        const allFeedbackData = [
          ...(q1Data.results || []),
          ...(q2Data.results || []),
          ...(q3Data.results || [])
        ];
        
        // Calculate health score
        const scoreData = calculateSiteHealthScore(allFeedbackData);
        setHealthData(scoreData);
      } catch (error) {
        console.error('Error fetching site health data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (siteId) {
      fetchData();
    }
  }, [siteId]);

  // Get status text and color based on the score
  const status = getHealthStatus(healthData.score);

  // Function to generate stroke dash array for circle
  const calculateStrokeDasharray = (score) => {
    const circumference = 2 * Math.PI * 40; // r = 40
    return `${(score / 100) * circumference} ${circumference}`;
  };

  return (
    <div className="chart-container bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Site Health Score</h3>
      
      {loading ? (
        <div className="h-full flex items-center justify-center">
          <p className="loading-message">Loading site health data...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="relative w-32 h-32 mb-2">
            {/* Background circle */}
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle 
                cx="50" 
                cy="50" 
                r="40" 
                fill="none" 
                stroke="#E5E7EB" 
                strokeWidth="10"
              />
              {/* Progress circle */}
              <circle 
                cx="50" 
                cy="50" 
                r="40" 
                fill="none" 
                stroke={status.color} 
                strokeWidth="10" 
                strokeDasharray={calculateStrokeDasharray(healthData.score)}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
            
            {/* Score text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold">{healthData.score}</span>
              <span className="text-sm text-gray-500">/100</span>
            </div>
          </div>
          
          <div className="text-center">
            <p className="font-medium text-lg" style={{ color: status.color }}>
              {status.text}
            </p>
            
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center justify-center">
                <span className={`font-medium ${healthData.trends.lastWeek >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formatTrend(healthData.trends.lastWeek)}
                </span>
                <span className="ml-1 text-gray-500">vs last week</span>
              </div>
              
              <div className="flex items-center justify-center">
                <span className={`font-medium ${healthData.trends.lastMonth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formatTrend(healthData.trends.lastMonth)}
                </span>
                <span className="ml-1 text-gray-500">vs last month</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteHealthScore;